/**
 * The structured geocode implementation. Creates a new instance of service call.
 *
 * Parameters can be passed on creation or provided via convenient functions that can be chained.
 *
 * The method go() performs the actual call. The call is asynchronous therefore user needs to pass callback function.
 *
 * @class structuredGeocode
 */
define('services/structuredGeocode',["rest/structuredGeocode", "model/geocode/geocode", "./chain", "./validators"],
    function (structuredGeocode, model, chain, validators) {
        "use strict";

        var fields = {
            /**
             * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
             * tomtom.key or tomtom.searchKey function.
             * But also per service instance on initialization or with this method.
             *
             * The API Key can be issued from <a href="http://developer.tomtom.com" target="_blank">http://developer.tomtom.com</a>.
             *
             * @method key
             * @chainable
             * @param {String} [key] key API Key valid for requested service.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _key_ value if
             *          argument is omitted.
             */
            key: {validator: validators.string},
            /**
             * A setter and getter for required _'countryCode'_ attribute. Represents 2 or 3 letter country code
             * (e.g.: FR, ES).
             *
             * @method countryCode
             * @chainable
             * @param {String} [countryCode] 2 or 3 letter country code.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _countryCode_
             *          value if argument is omitted.
             */
            countryCode: {validator: validators.countryCode, required: true},
            /**
             * A setter and getter for _'limit'_ attribute. Maximum number of responses that will be returned.
             * Default value is 10.
             *
             * @method limit
             * @chainable
             * @param {integer} [limit=10] Maximum number of responses that will be returned.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|integer} Method returns the service instance allowing chaining or _limit_ value if
             *     argument is omitted.
             */
            limit: {validator: validators.limit},
            /**
             * A setter and getter for _'offset'_ attribute. Represents starting offset of the returned results within the full
             * result set. Default value is 0.
             *
             * @method offset
             * @chainable
             * @param {integer} [offset=0] Starting offset of the returned results within the full result set.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|integer} Method returns the service instance allowing chaining or _offset_ value
             * if argument is omitted.
             */
            offset: {validator: validators.naturalInteger, name: "ofs"},
            /**
             * A setter and getter for _'streetNumber'_ attribute. Means street number for the structured address.
             *
             * @method streetNumber
             * @chainable
             * @param {String} [streetNumber] The street number for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _streetNumber_
             * value if argument is omitted.
             */
            streetNumber: {validator: validators.string},
            /**
             * A setter and getter for _'streetName'_ attribute. Represents street name for the structured address.
             *
             * @method streetName
             * @chainable
             * @param {String} [streetName] The street name for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _streetName_
             * value if argument is omitted.
             */
            streetName: {validator: validators.string},
            /**
             * A setter and getter for _'crossStreet'_ attribute. Represents cross street name for the structured address.
             *
             * @method crossStreet
             * @chainable
             * @param {String} [crossStreet] The cross street name for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _crossStreet_
             * value if argument is omitted.
             */
            crossStreet: {validator: validators.string},
            /**
             * A setter and getter for _'municipality'_ attribute. Represents municipality (city/town) for the
             * structured address.
             *
             * @method municipality
             * @chainable
             * @param {String} [municipality] The municipality (city/town) for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _municipality_
             * value if argument is omitted.
             */
            municipality: {validator: validators.string},
            /**
             * A setter and getter for _'municipalitySubdivision'_ attribute. Represents municipality subdivision
             * (sub/super city) for the structured address.
             *
             * @method municipalitySubdivision
             * @chainable
             * @param {String} [municipalitySubdivision] The municipality subdivision (sub/super city) for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _municipalitySubdivision_
             * value if argument is omitted.
             */
            municipalitySubdivision: {validator: validators.string},
            /**
             * A setter and getter for _'countryTertiarySubdivision'_ attribute. Represents named area for the structured
             * address.
             *
             * @method countryTertiarySubdivision
             * @chainable
             * @param {String} [countryTertiarySubdivision] The named area for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _countryTertiarySubdivision_
             * value if argument is omitted.
             */
            countryTertiarySubdivision: {validator: validators.string},
            /**
             * A setter and getter for _'countrySecondarySubdivision'_ attribute. Represents country for the structured
             * address.
             *
             * @method countrySecondarySubdivision
             * @chainable
             * @param {String} [countrySecondarySubdivision] The country for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _countrySecondarySubdivision_
             * value if argument is omitted.
             */
            countrySecondarySubdivision: {validator: validators.string},
            /**
             * A setter and getter for _'countrySubdivision'_ attribute. Represents state or province for the structured
             * address.
             *
             * @method countrySubdivision
             * @chainable
             * @param {String} [countrySubdivision] The state or province for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _countrySubdivision_
             * value if argument is omitted.
             */
            countrySubdivision: {validator: validators.string},
            /**
             * A setter and getter for _'postalCode'_ attribute. Represents zip code or postal code for the structured
             * address.
             *
             * @method postalCode
             * @chainable
             * @param {String} [postalCode] The zip code or postal code for the structured address.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _postalCode_
             * value if argument is omitted.
             */
            postalCode: {validator: validators.string},
            /**
             * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] Protocol type, one of _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|String} Method returns the service instance allowing chaining or _protocol_
             * value if argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(["http", "https"], "protocol")},
            /**
             * A setter and getter for _'best result'_ option. It makes the service return only one, best match result.
             * This option overwrites both limit and offset parameters. It changes the response from array to single
             * result. Default value is false.
             *
             * @method bestResult
             * @chainable
             * @param {Boolean} [bestResult] Defines whether structured geocode service should return the best result.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {structuredGeocode|Boolean} Method returns the service instance allowing chaining or _bestResult_
             * value if argument is omitted.
             */
            bestResult: {validator: validators.bool},
            baseUrl: {validator: validators.string},
            serviceUrl: {validator: validators.string}
        };

        /**
         * The function performing actual call to the web service. This method can be executed multiple times, it is
         * not changing parameters itself.
         *
         * Callback function takes a response parameter and fail callback takes error as only parameter.
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.searchKey(<Your API key>);
         * var structuredGeocode = tomtom.structuredGeocode().countryCode('GB').go(myFunction);
         * ```
         */
        /**
         * A setter and getter for callback function. This function is called after receiving successful response from
         * service. It takes one argument which is the response - JSON described in go() function documentation.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} if the given argument is not a function
         * @return {structuredGeocode} Method returns the service instance allowing chaining or function if argument is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {structuredGeocode|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */

        function checkBestResult(data) {
            var result = !!data.bestResult;
            delete data.bestResult;
            return result;
        }

        function handleChain(data, callback) {
            var bestResult = checkBestResult(data);
            if (bestResult) {
                data.limit = 1;
                data.offset = 0;
            }
            structuredGeocode.structuredGeocode(data, function (response) {
                if (bestResult) {
                    callback.call(this, model(response)[0]);
                } else {
                    callback.call(this, model(response));
                }
            });
        }

        /**
         * @function structuredGeocode
         *
         * @param {Object} [options] Options to be passed to the structured geocode call.
         * @param {String} [options.key] The API Key issued from <a
         *     href="http://developer.tomtom.com">http://developer.tomtom.com</a>.
         * @param {String} [options.countryCode] 2 or 3 letter country code (e.g.: FR, ES).
         * @param {Integer} [options.limit] Maximum number of responses that will be returned.
         * @param {Integer} [options.offset] Starting offset of the returned results within the full result set.
         * @param {String} [options.streetNumber] The street number for the structured address.
         * @param {String} [options.streetName] The street name for the structured address.
         * @param {String} [options.crossStreet] The cross street name for the structured address.
         * @param {String} [options.municipality] The municipality (city/town) for the structured address.
         * @param {String} [options.municipalitySubdivision] The municipality subdivision (sub/super city) for the structured
         *     address.
         * @param {String} [options.countryTertiarySubdivision] The named area for the structured address.
         * @param {String} [options.countrySecondarySubdivision] The county for the structured address.
         * @param {String} [options.countrySubdivision] The state or province for the structured address.
         * @param {String} [options.postalCode] The zip code or postal code for the structured address.
         * @param {Boolean} [options.bestResult] If the "bestResult" flag is set, service responses with single (best
         *     match) result.
         * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
         * @param {Function} [options.callback] Specifies the callback method. Only used where format is jsonp.
         * @param {Function} [options.fail] The callback function to be called on failure.
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * tomtom.searchKey(<Your API key>); // this is optional if you passed global key earlier
         * var structuredGeocode = tomtom.structuredGeocode()
         *   .countryCode('GB')
         *   .callback(myFunction).go();
         * ```
         */
        return function (options) {
            return chain(fields, handleChain, options);
        };
    });
